import AppTagStatus from '@nxsys/components/AppTagStatus';
import { PaymentSettingType } from '@nxsys/constants/AppEnums';
import { TypedMessages } from '@nxsys/hooks/useTypedIntl';
import {
  Deduction,
  Payment,
  PaymentRate,
  VATCode,
} from '@nxsys/store/slices/paymentSetting/types';
import { Icons } from '@nxsys/types/icons';
import {
  formatPaymentType,
  formatPaymentUnit,
  formatStatus,
} from '@nxsys/utils';
import { Tag, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';

export const getColumns = ({
  isBureau,
  isIreland,
  isUK,
  messages,
  paymentSettingType,
  handleViewPayment,
  handleViewDeduction,
  handleViewPaymentRate,
}: {
  isBureau?: boolean;
  isIreland?: boolean;
  isUK?: boolean;
  messages: TypedMessages;
  paymentSettingType: PaymentSettingType;
  handleViewPayment?: (data: Payment) => void;
  handleViewDeduction?: (data: Deduction) => void;
  handleViewPaymentRate?: (data: PaymentRate) => void;
}): ColumnsType<Payment | Deduction | PaymentRate | VATCode> => {
  if (
    paymentSettingType === PaymentSettingType.PAYMENT ||
    paymentSettingType === PaymentSettingType.DEDUCTION
  ) {
    return [
      {
        key: 'action',
        title: messages['common.action'],
        fixed: 'left',
        align: 'left',
        render: (record) => (
          <ul className="flex items-center justify-start gap-x-2">
            <li
              onClick={() => {
                if (paymentSettingType === PaymentSettingType.PAYMENT) {
                  handleViewPayment?.(record);
                } else {
                  handleViewDeduction?.(record);
                }
              }}
            >
              <Tooltip title="Edit">
                <span><Icons.IconEdit /></span>
              </Tooltip>
            </li>
          </ul>
        ),
        width: 100,
      },
      ...((paymentSettingType === PaymentSettingType.PAYMENT
        ? [
            {
              key: 'paymentCode',
              title: messages['paymentSetting.payment.paymentCode'],
              dataIndex: 'paymentCode',
              fixed: 'left',
            },
          ]
        : [
            {
              key: 'deductionCode',
              title: messages['paymentSetting.deduction.deductionCode'],
              dataIndex: 'deductionCode',
              fixed: 'left',
            },
          ]) as ColumnsType<Payment | Deduction | PaymentRate | VATCode>),
      {
        key: 'description',
        title: messages['common.description'],
        dataIndex: 'description',
      },
      ...((!isIreland
        ? [
            ...((!isBureau
              ? [
                  {
                    key: 'includeInCisCalculation',
                    title:
                      messages[
                        'paymentSetting.payment.includeInCisCalculation'
                      ],
                    dataIndex: 'includeInCisCalculation',
                    render: (record) => (
                      <Tag>
                        {record
                          ? messages['common.yes']
                          : messages['common.no']}
                      </Tag>
                    ),
                  },
                ]
              : []) as ColumnsType<
              Payment | Deduction | PaymentRate | VATCode
            >),
            {
              key: 'beforeTax',
              title: messages['paymentSetting.payment.beforeTax'],
              dataIndex: 'beforeTax',
              render: (record) => (
                <Tag>
                  {record ? messages['common.yes'] : messages['common.no']}
                </Tag>
              ),
            },
            {
              key: 'beforeNI',
              title: messages['paymentSetting.payment.beforeNI'],
              dataIndex: 'beforeNI',
              render: (record) => (
                <Tag>
                  {record ? messages['common.yes'] : messages['common.no']}
                </Tag>
              ),
            },
            {
              key: 'beforePension',
              title: messages['paymentSetting.payment.beforePension'],
              dataIndex: 'beforePension',
              render: (record) => (
                <Tag>
                  {record ? messages['common.yes'] : messages['common.no']}
                </Tag>
              ),
            },
          ]
        : [
            {
              key: 'taxCanBeDeducted',
              title: messages['paymentSetting.payment.beforeTax'],
              dataIndex: 'taxCanBeDeducted',
              render: (record) => (
                <Tag>
                  {record ? messages['common.yes'] : messages['common.no']}
                </Tag>
              ),
            },
            {
              key: 'employeePRSICanBeDeducted',
              title: messages['paymentSetting.payment.beforeEmployeePRSI'],
              dataIndex: 'employeePRSICanBeDeducted',
              render: (record) => (
                <Tag>
                  {record ? messages['common.yes'] : messages['common.no']}
                </Tag>
              ),
              width: 200,
            },
            {
              key: 'employerPRSICanBeDeducted',
              title: messages['paymentSetting.payment.beforeEmployerPRSI'],
              dataIndex: 'employerPRSICanBeDeducted',
              render: (record) => (
                <Tag>
                  {record ? messages['common.yes'] : messages['common.no']}
                </Tag>
              ),
              width: 200,
            },
            {
              key: 'uscCanBeDeducted',
              title: messages['paymentSetting.payment.beforeUSC'],
              dataIndex: 'uscCanBeDeducted',
              render: (record) => (
                <Tag>
                  {record ? messages['common.yes'] : messages['common.no']}
                </Tag>
              ),
            },
            {
              key: 'ascCanBeDeducted',
              title: messages['paymentSetting.payment.beforeASC'],
              dataIndex: 'ascCanBeDeducted',
              render: (record) => (
                <Tag>
                  {record ? messages['common.yes'] : messages['common.no']}
                </Tag>
              ),
            },
            {
              key: 'pensionSchemaDeductionCanBeDeducted',
              title:
                messages[
                  'paymentSetting.payment.beforePensionSchemeDeductions'
                ],
              dataIndex: 'pensionSchemaDeductionCanBeDeducted',
              render: (record) => (
                <Tag>
                  {record ? messages['common.yes'] : messages['common.no']}
                </Tag>
              ),
              width: 300,
            },
            ...((paymentSettingType === PaymentSettingType.DEDUCTION
              ? [
                  {
                    key: 'lptCanBeDeducted',
                    title: messages['paymentSetting.payment.beforeLPT'],
                    dataIndex: 'lptCanBeDeducted',
                    render: (record) => (
                      <Tag>
                        {record
                          ? messages['common.yes']
                          : messages['common.no']}
                      </Tag>
                    ),
                  },
                ]
              : []) as ColumnsType<
              Payment | Deduction | PaymentRate | VATCode
            >),
            {
              key: 'includeInCisCalculation',
              title: messages['paymentSetting.payment.beforeRCT'],
              dataIndex: 'includeInCisCalculation',
              render: (record) => (
                <Tag>
                  {record ? messages['common.yes'] : messages['common.no']}
                </Tag>
              ),
            },
          ]) as ColumnsType<Payment | Deduction | PaymentRate | VATCode>),
    ];
  }

  if (paymentSettingType === PaymentSettingType.PAYMENT_RATE) {
    return [
      {
        key: 'action',
        title: messages['common.action'],
        fixed: 'left',
        align: 'left',
        render: (record) => (
          <ul className="flex items-center justify-start gap-x-2">
            <li onClick={() => handleViewPaymentRate?.(record)}>
              <Tooltip title="Edit">
                <span><Icons.IconEdit /></span>
              </Tooltip>
            </li>
          </ul>
        ),
        width: 100,
      },
      {
        key: 'status',
        title: messages['common.status'],
        render: (record) => {
          const status = record?.status;
          return (
            <AppTagStatus className={`tag-popup ${formatStatus(status)}`}>
              {formatStatus(status)}
            </AppTagStatus>
          );
        },
        width: 110,
      },
      {
        key: 'code',
        title: messages['paymentSetting.paymentRate.code'],
        dataIndex: 'code',
        width: 130,
      },
      {
        key: 'description',
        title: messages['common.description'],
        dataIndex: 'description',
      },
      {
        key: 'type',
        title: messages['common.type'],
        dataIndex: 'type',
        render: (record) => formatPaymentType(record),
        width: 165,
      },
      {
        key: 'units',
        title: messages['common.units'],
        dataIndex: 'units',
        render: (record) => formatPaymentUnit(record),
        width: 120,
      },
      {
        key: 'hoursPerUnit',
        title: `${messages['common.hours']} / ${messages['common.unit']}`,
        dataIndex: 'hoursPerUnit',
        width: 130,
      },
      {
        key: 'displayOnPayslip',
        title: messages['paymentSetting.payment.displayOnPayslip'],
        dataIndex: 'displayOnPayslip',
        render: (record) => (
          <Tag>{record ? messages['common.yes'] : messages['common.no']}</Tag>
        ),
        width: 180,
      },
      // Pick the column set by country, as PaymentRateForm does: an Irish
      // umbrella tenant is not a bureau, but its rates carry the Irish flags.
      ...((!isIreland
        ? [
            {
              key: 'includeInCisCalculation',
              title: messages['paymentSetting.paymentRate.beforeCisTax'],
              dataIndex: 'includeInCisCalculation',
              render: (record) => (
                <Tag>
                  {record ? messages['common.yes'] : messages['common.no']}
                </Tag>
              ),
              width: 160,
            },
            {
              key: 'includeTaxCalculation',
              title: messages['paymentSetting.payment.beforeTax'],
              dataIndex: 'includeTaxCalculation',
              render: (record) => (
                <Tag>
                  {record ? messages['common.yes'] : messages['common.no']}
                </Tag>
              ),
              width: 130,
            },
            {
              key: 'includeNICalculation',
              title: messages['paymentSetting.payment.beforeNI'],
              dataIndex: 'includeNICalculation',
              render: (record) => (
                <Tag>
                  {record ? messages['common.yes'] : messages['common.no']}
                </Tag>
              ),
              width: 130,
            },
            {
              key: 'includePensionCalculation',
              title: messages['paymentSetting.payment.beforePension'],
              dataIndex: 'includePensionCalculation',
              render: (record) => (
                <Tag>
                  {record ? messages['common.yes'] : messages['common.no']}
                </Tag>
              ),
              width: 150,
            },
            {
              key: 'includeAccrueHolidayPay',
              title: messages['paymentSetting.paymentRate.accrueHolidayPay'],
              dataIndex: 'includeAccrueHolidayPay',
              render: (record) => (
                <Tag>
                  {record ? messages['common.yes'] : messages['common.no']}
                </Tag>
              ),
              width: 280,
            },
            {
              key: 'excludeFromNmwCalculation',
              title: 'Exclude from NMW Calculation',
              dataIndex: 'excludeFromNmwCalculation',
              render: (record) => (
                <Tag>
                  {record ? messages['common.yes'] : messages['common.no']}
                </Tag>
              ),
              width: 280,
            },
            ...((!isUK
              ? [
                  {
                    key: 'raiseInvoice',
                    title: messages['paymentSetting.paymentRate.raiseInvoice'],
                    dataIndex: 'raiseInvoice',
                    render: (record) => (
                      <Tag>
                        {record
                          ? messages['common.yes']
                          : messages['common.no']}
                      </Tag>
                    ),
                    width: 240,
                  },
                ]
              : []) as ColumnsType<
              Payment | Deduction | PaymentRate | VATCode
            >),
          ]
        : [
            {
              key: 'taxCanBeDeducted',
              title: messages['paymentSetting.payment.beforeTax'],
              dataIndex: 'taxCanBeDeducted',
              render: (record) => (
                <Tag>
                  {record ? messages['common.yes'] : messages['common.no']}
                </Tag>
              ),
              width: 130,
            },
            {
              key: 'employeePRSICanBeDeducted',
              title: messages['paymentSetting.payment.beforeEmployeePRSI'],
              dataIndex: 'employeePRSICanBeDeducted',
              render: (record) => (
                <Tag>
                  {record ? messages['common.yes'] : messages['common.no']}
                </Tag>
              ),
              width: 200,
            },
            {
              key: 'employerPRSICanBeDeducted',
              title: messages['paymentSetting.payment.beforeEmployerPRSI'],
              dataIndex: 'employerPRSICanBeDeducted',
              render: (record) => (
                <Tag>
                  {record ? messages['common.yes'] : messages['common.no']}
                </Tag>
              ),
              width: 200,
            },
            {
              key: 'uscCanBeDeducted',
              title: messages['paymentSetting.payment.beforeUSC'],
              dataIndex: 'uscCanBeDeducted',
              render: (record) => (
                <Tag>
                  {record ? messages['common.yes'] : messages['common.no']}
                </Tag>
              ),
              width: 130,
            },
            {
              key: 'ascCanBeDeducted',
              title: messages['paymentSetting.payment.beforeASC'],
              dataIndex: 'ascCanBeDeducted',
              render: (record) => (
                <Tag>
                  {record ? messages['common.yes'] : messages['common.no']}
                </Tag>
              ),
              width: 130,
            },
            {
              key: 'pensionSchemaDeductionCanBeDeducted',
              title:
                messages[
                  'paymentSetting.payment.beforePensionSchemeDeductions'
                ],
              dataIndex: 'pensionSchemaDeductionCanBeDeducted',
              render: (record) => (
                <Tag>
                  {record ? messages['common.yes'] : messages['common.no']}
                </Tag>
              ),
              width: 300,
            },
            {
              key: 'lptCanBeDeducted',
              title: messages['paymentSetting.payment.beforeLPT'],
              dataIndex: 'lptCanBeDeducted',
              render: (record) => (
                <Tag>
                  {record ? messages['common.yes'] : messages['common.no']}
                </Tag>
              ),
              width: 130,
            },
            {
              key: 'includeAccrueHolidayPay',
              title: messages['paymentSetting.paymentRate.accrueHolidayPay'],
              dataIndex: 'includeAccrueHolidayPay',
              render: (record) => (
                <Tag>
                  {record ? messages['common.yes'] : messages['common.no']}
                </Tag>
              ),
              width: 280,
            },
            {
              key: 'excludeFromNmwCalculation',
              title: 'Exclude from NMW Calculation',
              dataIndex: 'excludeFromNmwCalculation',
              render: (record) => (
                <Tag>
                  {record ? messages['common.yes'] : messages['common.no']}
                </Tag>
              ),
              width: 280,
            },
            {
              key: 'includeInCisCalculation',
              title: messages['paymentSetting.payment.beforeRCT'],
              dataIndex: 'includeInCisCalculation',
              render: (record) => (
                <Tag>
                  {record ? messages['common.yes'] : messages['common.no']}
                </Tag>
              ),
              width: 130,
            },
          ]) as ColumnsType<Payment | Deduction | PaymentRate | VATCode>),
    ];
  }

  if (paymentSettingType === PaymentSettingType.VAT_CODES) {
    return [
      {
        key: 'VATCode',
        title: messages['common.vatCode'],
        render: (record) => {
          const vatCode = record?.VATCode;
          return <Tag>{vatCode}</Tag>;
        },
      },
      {
        key: 'description',
        title: messages['common.description'],
        dataIndex: 'description',
      },
      {
        key: 'VATRate',
        title: messages['common.vatRate'],
        dataIndex: 'VATRate',
      },
    ];
  }

  return [];
};
